// useRecursos.js

import { useArloService } from '@/services/arloapi.js';
import { useErrorHandler } from '@/composables/useErrorHandler.js';
import { useCacheRequisicoesStore } from '@/stores/cacheRequisicoes';

/**
 * Prefixo das chaves de cache dos carregadores criados aqui. Serve para
 * invalidar em bloco sem esbarrar no cache de outras telas (ex: o dashboard).
 */
export const PREFIXO_CACHE_RECURSOS = 'recursos:';

/**
 * Construtor das funções de carregamento de dados da API.
 *
 * Evita repetir o mesmo `try/catch` em cada `carregarX` dos composables de API:
 * basta informar o destino (ref) e a URL — fixa ou montada a partir dos
 * argumentos recebidos na chamada.
 *
 * @example
 * const { criarCarregador } = useRecursos();
 *
 * const listaGrupos = ref([]);
 * const carregarGrupos = criarCarregador(listaGrupos, gruposEndpoint.listar, {
 *     mensagemErro: 'Não foi possível carregar os grupos'
 * });
 *
 * const usuario = ref(null);
 * const carregarUsuario = criarCarregador(usuario, (id) => usuariosEndpoint.detalhar(id));
 * await carregarUsuario(10);
 *
 * @example <caption>Quando o conteúdo vem dentro de um envelope</caption>
 * // resposta { items: [...], total: 12 } -> o destino recebe apenas o array
 * const carregarAnexos = criarCarregador(anexos, (id) => anexosEndpoint.listar(id), { extrair: 'items' });
 *
 * // resposta com formato próprio -> monte o valor do destino na mão
 * const carregarProcessosObservados = criarCarregador(processosObservados, url, {
 *     extrair: (resposta) => ({ items: resposta?.items || [], total: resposta?.total || 0 })
 * });
 *
 * @example <caption>Listas paginadas no servidor</caption>
 * // o destino fica com a página atual; a contagem geral vai para o ref de paginação
 * const paginacaoFuncionarios = ref({ page: 1, pages: 1, size: 0, total: 0 });
 * const carregarFuncionariosPaginados = criarCarregador(funcionarios, montarUrl, {
 *     extrair: 'items',
 *     paginacao: paginacaoFuncionarios
 * });
 *
 * @example <caption>Dados de referência, que quase não mudam</caption>
 * // busca uma vez e reaproveita: setores, cargos, tipos, status, campos de formulário
 * const carregarSetores = criarCarregador(setores, setoresEndpoint.setores.listar, { cache: true });
 *
 * // depois de criar/editar/apagar um setor, derrube o que foi guardado
 * const { invalidarCache } = useRecursos();
 * invalidarCache(setoresEndpoint.setores.listar);
 */
export function useRecursos() {
    const { getDados } = useArloService();
    const { handleApiError } = useErrorHandler();
    const cacheRequisicoes = useCacheRequisicoesStore();

    /**
     * Tira do envelope o conteúdo que vai para o destino.
     *
     * Com uma string, lê aquela chave da resposta (`'items'`, `'data'`, ...) e, se ela
     * não existir, usa a resposta inteira — vários endpoints ora devolvem
     * `{ items: [...] }`, ora o array puro. Com uma função, o valor é o que ela devolver.
     * Sem `extrair`, a resposta vai inteira para o destino.
     *
     * @param {any} resposta corpo devolvido pela API.
     * @param {string|Function} [extrair] chave do envelope ou função `(resposta) => valor`.
     */
    const extrairConteudo = (resposta, extrair) => {
        if (typeof extrair === 'function') return extrair(resposta);
        if (typeof extrair === 'string') return resposta?.[extrair] ?? resposta;
        return resposta;
    };

    /**
     * Separa os dados de paginação do envelope (`{ items, page, pages, size, total }`),
     * que o destino não recebe por só guardar os itens da página atual.
     *
     * @param {any} resposta corpo devolvido pela API.
     */
    const extrairPaginacao = (resposta) => ({
        page: resposta?.page ?? 1,
        pages: resposta?.pages ?? 1,
        size: resposta?.size ?? 0,
        total: resposta?.total ?? 0,
    });

    /**
     * Busca o corpo da resposta, passando ou não pelo cache com expiração.
     *
     * @param {string} url
     * @param {boolean|{ ttlMinutos?: number }} [cache] `true` usa o TTL padrão.
     */
    const buscarCorpo = async (url, cache) => {
        if (!cache) return (await getDados(url)).value;

        const { ttlMinutos } = cache === true ? {} : cache;

        return cacheRequisicoes.obterOuBuscar(
            `${PREFIXO_CACHE_RECURSOS}${url}`,
            async () => (await getDados(url)).value,
            { ttlMinutos }
        );
    };

    /**
     * Derruba o que está guardado, para que a próxima chamada vá ao servidor.
     * Use depois de criar/editar/apagar o recurso, ou num botão de atualizar.
     *
     * Como a chave é a URL completa, informar a URL base alcança também suas
     * variações — `.../setores` limpa `?nome=`, `/arvore` e `/paginados`.
     *
     * @param {string} [url] URL ou trecho inicial dela; sem argumento, limpa tudo.
     */
    const invalidarCache = (url = '') => cacheRequisicoes.invalidar(`${PREFIXO_CACHE_RECURSOS}${url}`);

    /**
     * @param {import('vue').Ref} destino ref que recebe a resposta.
     * @param {string|Function} url URL fixa ou função que monta a URL com os argumentos do carregador.
     * @param {{ extrair?: string|Function, paginacao?: import('vue').Ref, mensagemErro?: string, padrao?: any, quando?: Function, carregando?: import('vue').Ref, cache?: boolean|{ ttlMinutos?: number } }} [opcoes] `extrair` diz o que aproveitar da resposta antes de gravar no destino: o nome da chave do envelope (`'items'`, `'data'`) ou uma função `(resposta) => valor` para formatos próprios. `paginacao` é um ref que recebe `{ page, pages, size, total }` do envelope — use nas listas paginadas no servidor, em que o destino fica só com a página atual. `padrao` é usado quando a API não devolve conteúdo (por omissão, o valor inicial do próprio destino). `mensagemErro` substitui o texto padrão do toast. `quando` recebe os argumentos do carregador e, se devolver falso, a chamada nem sai. `carregando` é um ref booleano ligado durante a requisição e desligado ao final, mesmo com erro. `cache` guarda a resposta por URL e reaproveita nas chamadas seguintes — só para dados de referência, que quase não mudam; use `invalidarCache` ao alterá-los. O conteúdo guardado é o mesmo objeto entregue ao destino, então não o altere no lugar (prefira `filter`/`map` a `push`/`sort`).
     * @returns {(...args: any[]) => Promise<any>} carregador; devolve o valor carregado ou `undefined` em caso de erro.
     */
    const criarCarregador = (destino, url, opcoes = {}) => {
        const { extrair, paginacao, mensagemErro, padrao = destino.value, quando, carregando, cache } = opcoes;

        return async (...args) => {
            if (quando && !quando(...args)) return;

            if (carregando) carregando.value = true;

            try {
                const corpo = await buscarCorpo(typeof url === 'function' ? url(...args) : url, cache);
                destino.value = extrairConteudo(corpo, extrair) ?? padrao;
                if (paginacao) paginacao.value = extrairPaginacao(corpo);
                return destino.value;
            } catch (err) {
                // O destino mantém o valor anterior: quem trata o erro é o toast.
                handleApiError(err, mensagemErro ? { defaultMessage: mensagemErro } : {});
            } finally {
                if (carregando) carregando.value = false;
            }
        };
    };

    return { criarCarregador, invalidarCache };
}
